import { BLOG_BROWSE_TOPIC_KEYS, BLOG_FEATURED_IDS_BY_TOPIC } from '../components/blog/blogPostsData';

/** Default browse topics. `icon` is a static icon name from the shared commonIcon set. */
export const DEFAULT_BLOG_CATEGORIES = [
  { key: 'automation', name: 'AI & Automation', iconUrl: '', icon: 'workflow' },
  { key: 'strategy', name: 'Strategy & ROI', iconUrl: '', icon: 'target' },
  { key: 'analytics', name: 'Data & Analytics', iconUrl: '', icon: 'layers' },
  { key: 'cx', name: 'Customer Experience', iconUrl: '', icon: 'users' },
  { key: 'chatbot', name: 'AI Chatbot', iconUrl: '', icon: 'bot' },
  { key: 'ai-consulting', name: 'AI Consulting', iconUrl: '', icon: 'compass' },
  { key: 'neural', name: 'Neural Networks', iconUrl: '', icon: 'brain' },
  { key: 'solutions', name: 'AI Solutions', iconUrl: '', icon: 'chip' },
];

export function blogTopicLink(key) {
  return `/blog?topic=${encodeURIComponent(key)}`;
}

/** Infer topic from legacy featured-id map (bundled site). */
export function inferTopicKeyFromFeatured(postId, featured = BLOG_FEATURED_IDS_BY_TOPIC) {
  const id = String(postId);
  for (const [key, ids] of Object.entries(featured || {})) {
    if ((ids || []).map(String).includes(id)) return key;
  }
  return null;
}

/** Resolve browse-by-topic key: explicit field, featured map, then category/tag name. */
export function resolveBlogTopicKey(post, categories, featuredIdsByTopic) {
  const cats = normalizeBlogCategories(categories);
  const validKeys = new Set(cats.map((c) => c.key));
  if (post?.topicKey && validKeys.has(post.topicKey)) return post.topicKey;

  const featured =
    featuredIdsByTopic && Object.keys(featuredIdsByTopic).length
      ? featuredIdsByTopic
      : BLOG_FEATURED_IDS_BY_TOPIC;
  const fromFeatured = inferTopicKeyFromFeatured(post?.id, featured);
  if (fromFeatured && validKeys.has(fromFeatured)) return fromFeatured;

  const hay = `${post?.category || ''} ${post?.tag || ''}`.toLowerCase();
  const byName = cats.find((c) => {
    const name = String(c.name || '').toLowerCase();
    const keyBits = String(c.key || '').replace(/-/g, ' ');
    return (name.length > 2 && hay.includes(name)) || (keyBits && hay.includes(keyBits));
  });
  if (byName) return byName.key;

  return cats[0]?.key || BLOG_BROWSE_TOPIC_KEYS[0] || 'automation';
}

export function normalizeBlogCategories(categories) {
  if (!Array.isArray(categories) || !categories.length) {
    return DEFAULT_BLOG_CATEGORIES.map((c) => ({ ...c }));
  }
  return categories
    .filter((c) => c && c.key && c.name)
    .map((c) => ({
      key: String(c.key).trim(),
      name: String(c.name).trim(),
      iconUrl: c.iconUrl || '',
      icon: c.icon || DEFAULT_BLOG_CATEGORIES.find((d) => d.key === c.key)?.icon || 'layers',
    }));
}

export function countPostsInTopic(posts, topicKey) {
  return (posts || []).filter((p) => String(p.topicKey) === String(topicKey)).length;
}

export function getPostsForTopic(posts, topicKey, featuredIdsByTopic) {
  const list = posts || [];
  const key = String(topicKey);
  const byField = list.filter((p) => String(p.topicKey) === key);
  if (byField.length) {
    return [...byField].sort((a, b) => Number(b.id) - Number(a.id));
  }
  const ids = featuredIdsByTopic?.[key] || [];
  const byLegacy = ids
    .map((id) => list.find((p) => String(p.id) === String(id)))
    .filter(Boolean);
  return byLegacy;
}

export function ensurePostsHaveTopicKeys(posts, categories, featuredIdsByTopic) {
  const cats = normalizeBlogCategories(categories);
  const nameByKey = Object.fromEntries(cats.map((c) => [c.key, c.name]));

  return (posts || []).map((post) => {
    const topicKey = resolveBlogTopicKey(post, cats, featuredIdsByTopic);
    return {
      ...post,
      topicKey,
      category: nameByKey[topicKey] || post.category || cats.find((c) => c.key === topicKey)?.name || '',
    };
  });
}

const RELATED_STOPWORDS = new Set([
  'with',
  'from',
  'that',
  'this',
  'your',
  'into',
  'for',
  'and',
  'the',
  'are',
  'how',
  'why',
  'what',
]);

function relatedTitleTokens(title) {
  return String(title || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 3 && !RELATED_STOPWORDS.has(w));
}

function scoreRelatedCandidate(candidate, current) {
  let score = 0;
  if (candidate.tag && current.tag && candidate.tag === current.tag) score += 4;
  if (candidate.category && current.category && candidate.category === current.category) score += 2;
  const currentWords = new Set(relatedTitleTokens(current.title));
  for (const word of relatedTitleTokens(candidate.title)) {
    if (currentWords.has(word)) score += 2;
  }
  const idGap = Math.abs(Number(candidate.id) - Number(current.id));
  score += 8 / (1 + idGap);
  return score;
}

/**
 * Related posts for a blog detail page: same Browse-by-Topic first,
 * nearest neighbors in that topic (not always the oldest two ids).
 */
export function pickRelatedBlogPosts(current, posts, featuredIdsByTopic, categories, count = 2) {
  if (!current || !Array.isArray(posts) || !posts.length || count < 1) return [];

  const cats = normalizeBlogCategories(categories);
  const featured = featuredIdsByTopic || BLOG_FEATURED_IDS_BY_TOPIC;
  const currentTopic = resolveBlogTopicKey(current, cats, featured);
  const others = posts.filter((p) => String(p.id) !== String(current.id));
  const picked = [];
  const seen = new Set();

  const pushUnique = (list) => {
    for (const post of list) {
      if (picked.length >= count) return;
      const id = String(post.id);
      if (seen.has(id)) continue;
      picked.push(post);
      seen.add(id);
    }
  };

  const topicPosts = getPostsForTopic(
    others.map((p) => ({ ...p, topicKey: resolveBlogTopicKey(p, cats, featured) })),
    currentTopic,
    featured
  ).sort((a, b) => {
    const da = Math.abs(Number(a.id) - Number(current.id));
    const db = Math.abs(Number(b.id) - Number(current.id));
    if (da !== db) return da - db;
    return scoreRelatedCandidate(b, current) - scoreRelatedCandidate(a, current);
  });
  pushUnique(topicPosts);

  if (picked.length < count) {
    const rest = others
      .filter((p) => !seen.has(String(p.id)))
      .sort((a, b) => {
        const diff = scoreRelatedCandidate(b, current) - scoreRelatedCandidate(a, current);
        if (diff) return diff;
        return Math.abs(Number(a.id) - Number(current.id)) - Math.abs(Number(b.id) - Number(current.id));
      });
    pushUnique(rest);
  }

  return picked.slice(0, count);
}

export function syncBrowseTopicKeys(categories) {
  return normalizeBlogCategories(categories).map((c) => c.key);
}
